import { useState } from 'react';
import './HostTutorial.css';

const STEPS = [
  {
    title: 'Welcome, Host! 🎉',
    body: `You control everything. The game screen (TV or main display) is read-only — players can't click anything on it. Only you can select clues, reveal answers, score points, and advance rounds.\n\nOpen this host panel on your phone or laptop. Open the game screen URL on the TV.`,
  },
  {
    title: 'Starting the Game',
    body: `The game screen shows a splash page until you start. When everyone is seated, press ▶ Start Game at the bottom of this page.\n\nAlternatively, clicking any clue in the BOARD section will start the game automatically and jump straight to that clue.`,
  },
  {
    title: 'Selecting Clues',
    body: `The game screen board is display-only — contestants call out a category and value, then you click that cell in the BOARD section here.\n\nThe clue appears on the game screen instantly. Played clues are grayed out. Use the Single / Double / Final tabs to navigate rounds.`,
  },
  {
    title: 'Buzzers',
    body: `Each team has a QR code in the TEAMS section. Players scan it on their phones, enter their name, and get a dedicated buzz button.\n\nWhen a clue is active, buzzers are automatically armed. Each registered buzzer shows up in the 📱 count per team. Names are unique — no impersonating teammates!\n\nDaily Doubles disable buzzers; use manual scoring instead.`,
  },
  {
    title: 'Buzz Timing',
    body: `When a player buzzes in, you have 5 seconds to judge before the timer expires.\n\n• ✓ Correct — score is awarded, question is done\n• ✗ Wrong — team is locked out, buzzers re-arm for other teams\n\nIf the timer expires before you judge, the player moves to "pending" — you can still mark them ✓/✗, while other teams can buzz simultaneously. All three teams wrong = buzzers disarm.`,
  },
  {
    title: 'Revealing the Answer',
    body: `The game screen never auto-reveals the answer. Only you can show it.\n\nIn the ACTIVE CLUE card, use the 👁 Reveal Answer on Screen button after a question is done. This shows the answer on the game screen for everyone to see.\n\nYou can also use this proactively if nobody buzzed and you want to move on.`,
  },
  {
    title: 'Scoring',
    body: `In the TEAMS section, each team has:\n• Amount input — pre-fills with the clue value; edit for Daily Double wagers\n• ✓ button — awards the amount\n• ✗ button — deducts the amount\n• Click the score to type a new value directly\n\nFor buzzer judging, use the ✓/✗ buttons that appear in the ACTIVE CLUE section — those automatically award the right amount.`,
  },
  {
    title: 'Jumping Between Rounds',
    body: `Use the JUMP TO ROUND buttons to move the game screen to a new round. The current round is highlighted.\n\nThe game screen shows the category reveal animation for each new round. You control when to proceed — there are no "Next Round" buttons on the game screen.`,
  },
  {
    title: 'Test Buzzers',
    body: `Each team card has a 🔔 Test Buzz button. Use it to simulate a buzz from that team during setup or testing — no phone required.\n\nTest buzzes work exactly like real buzzes: they arm, timeout, and get judged the same way.`,
  },
  {
    title: 'You\'re ready! 🥂',
    body: `A few tips for the party:\n• After each answer, invite Gamy to tell the real story — half the fun is hearing her version.\n• Wrong answers should be celebrated, not mocked. This is a tribute!\n• The host can add "…as Gamy's memoirs tell it" when reading sensitive clues.\n\nHappy 90th, Gamy! 🎂`,
  },
];

interface Props {
  onClose: () => void;
}

export default function HostTutorial({ onClose }: Props) {
  const [step, setStep] = useState(0);
  const current = STEPS[step];

  return (
    <div className="tutorial-overlay" onClick={onClose}>
      <div className="tutorial-modal" onClick={e => e.stopPropagation()}>
        <div className="tutorial-progress">
          {STEPS.map((_, i) => (
            <button
              key={i}
              className={`tutorial-dot ${i === step ? 'tutorial-dot-active' : ''} ${i < step ? 'tutorial-dot-done' : ''}`}
              onClick={() => setStep(i)}
              aria-label={`Step ${i + 1}`}
            />
          ))}
        </div>

        <div className="tutorial-step-label">Step {step + 1} of {STEPS.length}</div>
        <div className="tutorial-title">{current.title}</div>
        <div className="tutorial-body">
          {current.body.split('\n').map((line, i) => (
            <p key={i}>{line}</p>
          ))}
        </div>

        <div className="tutorial-nav">
          <button
            className="tutorial-btn tutorial-btn-secondary"
            onClick={() => setStep(s => Math.max(0, s - 1))}
            disabled={step === 0}
          >
            ← Back
          </button>

          {step < STEPS.length - 1 ? (
            <button
              className="tutorial-btn tutorial-btn-primary"
              onClick={() => setStep(s => s + 1)}
            >
              Next →
            </button>
          ) : (
            <button
              className="tutorial-btn tutorial-btn-primary"
              onClick={onClose}
            >
              Let's go! 🎉
            </button>
          )}
        </div>

        <button className="tutorial-close" onClick={onClose} aria-label="Close tutorial">✕</button>
      </div>
    </div>
  );
}
